import { Badge, Button, EmptyStateHelp, LearnMoreLink } from '@auctisync/ui';
import { dollarsToCents, formatCents, type Tables } from '@auctisync/shared';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type InventoryRow = Tables<'inventory_items'> & {
  category: Pick<Tables<'item_categories'>, 'id' | 'name'> | null;
  consignor: Pick<Tables<'consignors'>, 'id' | 'display_name'> | null;
};

type CategoryOption = Pick<Tables<'item_categories'>, 'id' | 'name'>;
type ConsignorOption = Pick<Tables<'consignors'>, 'id' | 'display_name'>;

const PAGE_SIZE = 25;

/**
 * Inventory list view. Server-paginated, searchable, filterable.
 *
 * Scope:
 *   - Search across title / title_override / control_number / MPN / SKU / UPC
 *   - Filter by category, consignor
 *   - Toggle archived visibility
 *   - Sort by updated_at desc (future: click column headers)
 *   - Pagination: 25 rows per page
 *
 * Writes (create / edit / delete) live on the detail route.
 */
export function AdminInventoryList() {
  const [items, setItems] = useState<InventoryRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [consignorFilter, setConsignorFilter] = useState<string>('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(0);

  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [consignors, setConsignors] = useState<ConsignorOption[]>([]);

  // Bulk selection across the current page
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeBulkAction, setActiveBulkAction] = useState<'category' | 'price' | 'archive' | null>(
    null,
  );
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);

  // Reset bulk selection whenever the underlying page of items changes
  useEffect(() => {
    setSelectedIds(new Set());
    setActiveBulkAction(null);
  }, [search, categoryFilter, consignorFilter, includeArchived, page]);

  // Debounce search input to 250ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load filter dropdown data once
  useEffect(() => {
    void (async () => {
      const [{ data: cats }, { data: cons }] = await Promise.all([
        supabase
          .from('item_categories')
          .select('id, name')
          .is('archived_at', null)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true }),
        supabase
          .from('consignors')
          .select('id, display_name')
          .eq('status', 'active')
          .order('display_name', { ascending: true })
          .limit(200),
      ]);
      setCategories(cats ?? []);
      setConsignors(cons ?? []);
    })();
  }, []);

  // Load paginated inventory rows whenever filters change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void (async () => {
      let query = supabase
        .from('inventory_items')
        .select(
          'id, control_number, title, title_override, description, category_id, consignor_id, qty_on_hand, qty_on_auction, qty_sold, starting_bid_cents, reserve_cents, retail_price_cents, is_visible, archived_at, created_at, updated_at, tenant_id, mpn, msrp_cents, upc, sku, location_bin, location_notes, condition, estimate_min_cents, estimate_max_cents, bid_increment_scale_id, custom_fields, description_override, category:item_categories(id, name), consignor:consignors(id, display_name)',
          { count: 'exact' },
        );

      if (!includeArchived) query = query.is('archived_at', null);
      if (categoryFilter) query = query.eq('category_id', categoryFilter);
      if (consignorFilter) query = query.eq('consignor_id', consignorFilter);

      const trimmed = search.trim();
      if (trimmed) {
        const escaped = trimmed.replace(/[%_,]/g, (c) => `\\${c}`);
        const pattern = `%${escaped}%`;
        query = query.or(
          [
            `title.ilike.${pattern}`,
            `title_override.ilike.${pattern}`,
            `control_number.ilike.${pattern}`,
            `mpn.ilike.${pattern}`,
            `sku.ilike.${pattern}`,
            `upc.ilike.${pattern}`,
          ].join(','),
        );
      }

      query = query.order('updated_at', { ascending: false });
      query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      const { data, count, error: err } = await query;
      if (cancelled) return;

      if (err) {
        setError(err.message);
        setItems([]);
        setTotalCount(0);
      } else {
        setItems((data ?? []) as InventoryRow[]);
        setTotalCount(count ?? 0);
        setError(null);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [search, categoryFilter, consignorFilter, includeArchived, page]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasResults = items.length > 0;
  const hasFilters = Boolean(search || categoryFilter || consignorFilter || includeArchived);
  const allVisibleSelected = items.length > 0 && items.every((item) => selectedIds.has(item.id));
  const selectedCount = selectedIds.size;

  function toggleAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const item of items) next.delete(item.id);
      } else {
        for (const item of items) next.add(item.id);
      }
      return next;
    });
  }

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function refreshItems() {
    let query = supabase
      .from('inventory_items')
      .select(
        'id, control_number, title, title_override, description, category_id, consignor_id, qty_on_hand, qty_on_auction, qty_sold, starting_bid_cents, reserve_cents, retail_price_cents, is_visible, archived_at, created_at, updated_at, tenant_id, mpn, msrp_cents, upc, sku, location_bin, location_notes, condition, estimate_min_cents, estimate_max_cents, bid_increment_scale_id, custom_fields, description_override, category:item_categories(id, name), consignor:consignors(id, display_name)',
        { count: 'exact' },
      );
    if (!includeArchived) query = query.is('archived_at', null);
    if (categoryFilter) query = query.eq('category_id', categoryFilter);
    if (consignorFilter) query = query.eq('consignor_id', consignorFilter);
    const trimmed = search.trim();
    if (trimmed) {
      const escaped = trimmed.replace(/[%_,]/g, (c) => `\\${c}`);
      const pattern = `%${escaped}%`;
      query = query.or(
        [
          `title.ilike.${pattern}`,
          `title_override.ilike.${pattern}`,
          `control_number.ilike.${pattern}`,
          `mpn.ilike.${pattern}`,
          `sku.ilike.${pattern}`,
          `upc.ilike.${pattern}`,
        ].join(','),
      );
    }
    query = query.order('updated_at', { ascending: false });
    query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    const { data, count, error: err } = await query;
    if (err) setError(err.message);
    else {
      setItems((data ?? []) as InventoryRow[]);
      setTotalCount(count ?? 0);
    }
  }

  async function bulkApplyCategory(categoryId: string | null) {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    setBulkMsg(null);
    const { error: err } = await supabase
      .from('inventory_items')
      .update({ category_id: categoryId })
      .in('id', Array.from(selectedIds));
    setBulkBusy(false);
    if (err) {
      setBulkMsg(`Error: ${err.message}`);
      return;
    }
    setBulkMsg(`Updated ${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'}.`);
    setActiveBulkAction(null);
    await refreshItems();
    setTimeout(() => setBulkMsg(null), 3000);
  }

  async function bulkApplyStartingBid(dollars: string) {
    if (selectedIds.size === 0) return;
    const cents = dollars.trim() === '' ? null : dollarsToCents(Number(dollars));
    setBulkBusy(true);
    setBulkMsg(null);
    const { error: err } = await supabase
      .from('inventory_items')
      .update({ starting_bid_cents: cents })
      .in('id', Array.from(selectedIds));
    setBulkBusy(false);
    if (err) {
      setBulkMsg(`Error: ${err.message}`);
      return;
    }
    setBulkMsg(
      `Updated starting bid on ${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'}.`,
    );
    setActiveBulkAction(null);
    await refreshItems();
    setTimeout(() => setBulkMsg(null), 3000);
  }

  async function bulkArchive() {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `Archive ${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'}? They will be hidden from the default list but not deleted.`,
      )
    ) {
      return;
    }
    setBulkBusy(true);
    setBulkMsg(null);
    const { error: err } = await supabase
      .from('inventory_items')
      .update({ archived_at: new Date().toISOString() })
      .in('id', Array.from(selectedIds));
    setBulkBusy(false);
    if (err) {
      setBulkMsg(`Error: ${err.message}`);
      return;
    }
    setBulkMsg(`Archived ${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'}.`);
    setActiveBulkAction(null);
    setSelectedIds(new Set());
    await refreshItems();
    setTimeout(() => setBulkMsg(null), 3000);
  }

  const rangeLabel = useMemo(() => {
    if (totalCount === 0) return '0 items';
    const start = page * PAGE_SIZE + 1;
    const end = Math.min((page + 1) * PAGE_SIZE, totalCount);
    return `${start}–${end} of ${totalCount}`;
  }, [page, totalCount]);

  return (
    <div>
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
          marginBottom: '1.25rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Inventory
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.35rem' }}>
            Your persistent catalog. Items flow from here into auctions as lots.
          </p>
          <LearnMoreLink helpSlug="inventory-overview" newTab>
            How inventory works
          </LearnMoreLink>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button variant="outline" asChild>
            <Link to="/admin/inventory/import">Import CSV</Link>
          </Button>
          <Button asChild>
            <Link to="/admin/inventory/new">+ New item</Link>
          </Button>
        </div>
      </header>

      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: '1rem',
          padding: '0.75rem',
          background: 'var(--surface-elev)',
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
        }}
      >
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search title, control #, MPN, SKU, UPC…"
          style={{
            flex: '1 1 18rem',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: '0.875rem',
          }}
        />
        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setPage(0);
          }}
          style={selectStyle}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={consignorFilter}
          onChange={(e) => {
            setConsignorFilter(e.target.value);
            setPage(0);
          }}
          style={selectStyle}
          aria-label="Filter by consignor"
        >
          <option value="">All consignors</option>
          {consignors.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name}
            </option>
          ))}
        </select>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            fontSize: '0.875rem',
            color: 'var(--text-muted)',
          }}
        >
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setIncludeArchived(e.target.checked);
              setPage(0);
            }}
          />
          Include archived
        </label>
      </div>

      {error && (
        <div
          style={{
            padding: '0.75rem 1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-danger, #fef2f2)',
            color: 'var(--text-danger, #991b1b)',
            border: '1px solid var(--border-danger, #fecaca)',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
          role="alert"
        >
          Failed to load inventory: {error}
        </div>
      )}

      {selectedCount > 0 && (
        <BulkToolbar
          count={selectedCount}
          busy={bulkBusy}
          activeAction={activeBulkAction}
          onSetAction={setActiveBulkAction}
          onClear={() => setSelectedIds(new Set())}
          onApplyCategory={bulkApplyCategory}
          onApplyStartingBid={bulkApplyStartingBid}
          onArchive={bulkArchive}
          categories={categories}
        />
      )}

      {bulkMsg && (
        <div
          role="status"
          style={{
            padding: '0.5rem 0.75rem',
            marginBottom: '1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: bulkMsg.startsWith('Error')
              ? 'var(--surface-danger, #fef2f2)'
              : 'var(--surface-success, #f0fdf4)',
            color: bulkMsg.startsWith('Error')
              ? 'var(--text-danger, #991b1b)'
              : 'var(--text-success, #166534)',
            border: '1px solid var(--admin-hairline, var(--border))',
            fontSize: '0.85rem',
          }}
        >
          {bulkMsg}
        </div>
      )}

      {!loading && !hasResults && !hasFilters && (
        <EmptyStateHelp
          title="No inventory yet"
          description="Create your first item to start cataloging. Items persist across auctions and quantities are tracked automatically."
          helpSlug="inventory-overview"
          primaryActionLabel="Create an item"
          primaryActionHref="/admin/inventory/new"
        />
      )}

      {!loading && !hasResults && hasFilters && (
        <div
          style={{
            padding: '3rem 1rem',
            textAlign: 'center',
            color: 'var(--text-muted)',
            border: '1px dashed var(--border)',
            borderRadius: 'var(--r-card, 0.5rem)',
          }}
        >
          <p style={{ marginBottom: '0.5rem', color: 'var(--text)' }}>
            No items match these filters.
          </p>
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setCategoryFilter('');
              setConsignorFilter('');
              setIncludeArchived(false);
              setPage(0);
            }}
            style={{
              color: 'var(--brand-accent)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
            }}
          >
            Clear filters
          </button>
        </div>
      )}

      {(loading || hasResults) && (
        <div
          style={{
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: 'var(--r-card, 0.5rem)',
            overflow: 'hidden',
            background: 'var(--surface-elev)',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr
                  style={{
                    background: 'var(--surface-muted)',
                    borderBottom: '1px solid var(--admin-hairline, var(--border))',
                  }}
                >
                  <th style={{ ...thStyle, width: '2.25rem' }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      aria-label="Select all on this page"
                    />
                  </th>
                  <th style={thStyle}>Control #</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Consignor</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Starting bid</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
                  : items.map((item) => (
                      <InventoryRowView
                        key={item.id}
                        item={item}
                        selected={selectedIds.has(item.id)}
                        onToggle={() => toggleOne(item.id)}
                      />
                    ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(hasResults || totalCount > PAGE_SIZE) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1rem',
            fontSize: '0.875rem',
            color: 'var(--text-muted)',
          }}
        >
          <span>{rangeLabel}</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={pagerButtonStyle(page === 0)}
            >
              ← Prev
            </button>
            <span style={{ alignSelf: 'center' }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              style={pagerButtonStyle(page >= totalPages - 1)}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InventoryRowView({
  item,
  selected,
  onToggle,
}: {
  item: InventoryRow;
  selected: boolean;
  onToggle: () => void;
}) {
  const displayTitle = item.title_override ?? item.title ?? '—';
  const categoryName = item.category?.name ?? '—';
  const consignorName = item.consignor?.display_name ?? '—';
  const qty = item.qty_on_hand;
  const updatedLabel = formatRelative(item.updated_at);

  return (
    <tr
      style={{
        borderBottom: '1px solid var(--admin-hairline, var(--border))',
        background: selected ? 'var(--surface-muted)' : undefined,
      }}
    >
      <td style={{ ...tdStyle, width: '2.25rem' }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${displayTitle}`}
        />
      </td>
      <td style={tdStyle}>
        <Link
          to={`/admin/inventory/${item.id}`}
          style={{ color: 'var(--brand-accent)', fontFamily: 'var(--font-mono, monospace)' }}
        >
          {item.control_number ?? '—'}
        </Link>
      </td>
      <td style={tdStyle}>
        <Link to={`/admin/inventory/${item.id}`} style={{ color: 'var(--text)' }}>
          {displayTitle}
        </Link>
      </td>
      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{categoryName}</td>
      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{consignorName}</td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>{qty}</td>
      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {item.starting_bid_cents !== null ? formatCents(item.starting_bid_cents) : '—'}
      </td>
      <td style={tdStyle}>
        {item.archived_at ? (
          <Badge variant="outline">Archived</Badge>
        ) : item.is_visible ? (
          <Badge variant="success">Visible</Badge>
        ) : (
          <Badge variant="outline">Hidden</Badge>
        )}
      </td>
      <td
        style={{
          ...tdStyle,
          textAlign: 'right',
          color: 'var(--text-muted)',
          whiteSpace: 'nowrap',
        }}
      >
        {updatedLabel}
      </td>
    </tr>
  );
}

function SkeletonRow() {
  return (
    <tr style={{ borderBottom: '1px solid var(--admin-hairline, var(--border))' }}>
      {Array.from({ length: 9 }).map((_, i) => (
        <td key={i} style={tdStyle}>
          <span
            style={{
              display: 'inline-block',
              height: '0.875rem',
              width: `${40 + Math.random() * 40}%`,
              background: 'var(--surface-muted)',
              borderRadius: '0.25rem',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

function BulkToolbar({
  count,
  busy,
  activeAction,
  onSetAction,
  onClear,
  onApplyCategory,
  onApplyStartingBid,
  onArchive,
  categories,
}: {
  count: number;
  busy: boolean;
  activeAction: 'category' | 'price' | 'archive' | null;
  onSetAction: (a: 'category' | 'price' | 'archive' | null) => void;
  onClear: () => void;
  onApplyCategory: (categoryId: string | null) => Promise<void> | void;
  onApplyStartingBid: (dollars: string) => Promise<void> | void;
  onArchive: () => Promise<void> | void;
  categories: CategoryOption[];
}) {
  const [categoryValue, setCategoryValue] = useState<string>('');
  const [priceValue, setPriceValue] = useState<string>('');

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        marginBottom: '1rem',
        padding: '0.75rem',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--brand-primary)',
        color: 'var(--brand-primary-fg)',
        display: 'grid',
        gap: '0.5rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <strong>
          {count} item{count === 1 ? '' : 's'} selected
        </strong>
        <ToolbarButton onClick={() => onSetAction('category')} active={activeAction === 'category'}>
          Change category
        </ToolbarButton>
        <ToolbarButton onClick={() => onSetAction('price')} active={activeAction === 'price'}>
          Set starting bid
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            void onArchive();
          }}
          disabled={busy}
        >
          Archive
        </ToolbarButton>
        <button
          type="button"
          onClick={onClear}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.4)',
            color: 'inherit',
            padding: '0.35rem 0.75rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            fontSize: '0.8rem',
            cursor: 'pointer',
          }}
        >
          Clear selection
        </button>
      </div>

      {activeAction === 'category' && (
        <div style={bulkActionRowStyle}>
          <select
            value={categoryValue}
            onChange={(e) => setCategoryValue(e.target.value)}
            style={bulkSelectStyle}
          >
            <option value="">— Clear category —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              void onApplyCategory(categoryValue || null);
            }}
          >
            Apply to {count}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => onSetAction(null)}>
            Cancel
          </Button>
        </div>
      )}

      {activeAction === 'price' && (
        <div style={bulkActionRowStyle}>
          <div style={{ position: 'relative' }}>
            <span
              style={{
                position: 'absolute',
                left: '0.6rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
                fontSize: '0.875rem',
              }}
            >
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={priceValue}
              onChange={(e) => setPriceValue(e.target.value)}
              placeholder="0.00 (blank = clear)"
              style={{ ...bulkSelectStyle, paddingLeft: '1.4rem', minWidth: '10rem' }}
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              void onApplyStartingBid(priceValue);
            }}
          >
            Apply to {count}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => onSetAction(null)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

function ToolbarButton({
  children,
  onClick,
  active,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: active ? 'rgba(255,255,255,0.25)' : 'transparent',
        border: '1px solid rgba(255,255,255,0.4)',
        color: 'inherit',
        padding: '0.35rem 0.75rem',
        borderRadius: 'var(--r-tile, 0.375rem)',
        fontSize: '0.8rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {children}
    </button>
  );
}

const bulkActionRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flexWrap: 'wrap',
};

const bulkSelectStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid rgba(255,255,255,0.4)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  minWidth: '12rem',
};

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffHours = diffMs / 3_600_000;
  if (diffHours < 1) return `${Math.max(1, Math.round(diffMs / 60_000))}m ago`;
  if (diffHours < 24) return `${Math.round(diffHours)}h ago`;
  const diffDays = diffHours / 24;
  if (diffDays < 30) return `${Math.round(diffDays)}d ago`;
  return d.toLocaleDateString();
}

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  verticalAlign: 'middle',
};

const selectStyle: React.CSSProperties = {
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  minWidth: '11rem',
};

function pagerButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '0.4rem 0.75rem',
    border: '1px solid var(--admin-hairline, var(--border))',
    borderRadius: 'var(--r-tile, 0.375rem)',
    background: 'var(--surface)',
    color: disabled ? 'var(--text-muted)' : 'var(--text)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    fontSize: '0.875rem',
  };
}
